import { siteConfig } from './site-config.ts'

export interface JsonLdGraph {
  '@context': 'https://schema.org'
  '@graph': Array<Record<string, unknown>>
}

export const defaultOgImage = `${siteConfig.url}/images/home-screen-image.webp`

export function publicPageCanonical(path: string) {
  if (path === '/') return `${siteConfig.url}/`
  return `${siteConfig.url}${path}`
}

export function publicPageHead(input: {
  title: string
  description: string
  path: string
  ogTitle?: string
  ogDescription?: string
  robots?: string
}) {
  const canonical = publicPageCanonical(input.path)
  const ogTitle = input.ogTitle ?? input.title
  const ogDescription = input.ogDescription ?? input.description

  return {
    title: input.title,
    meta: [
      { title: input.title },
      { name: 'description', content: input.description },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: siteConfig.name },
      { property: 'og:title', content: ogTitle },
      { property: 'og:description', content: ogDescription },
      { property: 'og:url', content: canonical },
      { property: 'og:image', content: defaultOgImage },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: ogTitle },
      { name: 'twitter:description', content: ogDescription },
      { name: 'twitter:image', content: defaultOgImage },
      { name: 'robots', content: input.robots ?? 'index,follow' },
    ],
    links: [{ rel: 'canonical', href: canonical }],
  }
}

export function getHomeJsonLd(): JsonLdGraph {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AutoRepair',
        '@id': `${siteConfig.url}/#business`,
        name: siteConfig.name,
        legalName: siteConfig.legalName,
        url: siteConfig.url,
        telephone: siteConfig.phone,
        email: siteConfig.email,
        address: {
          '@type': 'PostalAddress',
          streetAddress: siteConfig.address,
          addressLocality: siteConfig.locality,
          addressRegion: siteConfig.region,
          postalCode: siteConfig.postalCode,
          addressCountry: 'AU',
        },
        areaServed: ['Drysdale', 'Bellarine Peninsula', 'Geelong'],
        // Marketing hours are unpublished-confirmed. Do not treat this as bookable availability.
        openingHours: 'Mo-Fr 09:00-17:30',
        priceRange: '$$',
      },
      {
        '@type': 'WebSite',
        '@id': `${siteConfig.url}/#website`,
        name: siteConfig.name,
        url: siteConfig.url,
      },
      {
        '@type': 'Service',
        '@id': `${siteConfig.url}/ceramic-protection#service`,
        name: 'GYEON Ceramic Coating and Paint Protection',
        url: `${siteConfig.url}/ceramic-protection`,
        provider: { '@id': `${siteConfig.url}/#business` },
        areaServed: ['Geelong', 'Drysdale', 'Bellarine Peninsula'],
      },
      {
        '@type': 'Service',
        '@id': `${siteConfig.url}/paint-correction#service`,
        name: 'Paint Correction',
        url: `${siteConfig.url}/paint-correction`,
        provider: { '@id': `${siteConfig.url}/#business` },
        areaServed: ['Geelong', 'Drysdale', 'Bellarine Peninsula'],
      },
      {
        '@type': 'Service',
        '@id': `${siteConfig.url}/detailing#service`,
        name: 'Professional Vehicle Detailing',
        url: `${siteConfig.url}/detailing`,
        provider: { '@id': `${siteConfig.url}/#business` },
        areaServed: ['Geelong', 'Drysdale', 'Bellarine Peninsula'],
      },
    ],
  }
}

export function getServicePageJsonLd(
  type: 'ceramic' | 'detailing' | 'paintCorrection',
): JsonLdGraph {
  const pages = {
    ceramic: { path: '/ceramic-protection', name: 'GYEON Ceramic Coating Geelong' },
    detailing: { path: '/detailing', name: 'Car Detailing Geelong' },
    paintCorrection: { path: '/paint-correction', name: 'Paint Correction Geelong' },
  } as const
  const { path, name } = pages[type]

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        '@id': `${siteConfig.url}${path}#breadcrumbs`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteConfig.url },
          { '@type': 'ListItem', position: 2, name, item: `${siteConfig.url}${path}` },
        ],
      },
      {
        '@type': 'Service',
        '@id': `${siteConfig.url}${path}#service`,
        name,
        url: `${siteConfig.url}${path}`,
        provider: {
          '@type': 'AutoRepair',
          '@id': `${siteConfig.url}/#business`,
          name: siteConfig.name,
          telephone: siteConfig.phone,
          email: siteConfig.email,
        },
        areaServed: ['Geelong', 'Drysdale', 'Bellarine Peninsula'],
      },
    ],
  }
}

export function getGalleryJsonLd(): JsonLdGraph {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'ImageGallery',
        '@id': `${siteConfig.url}/gallery#gallery`,
        name: 'Autoglow Detailing Studio Gallery',
        url: `${siteConfig.url}/gallery`,
        about: 'Ceramic coating, paint correction and detailing work from Autoglow in Drysdale, Geelong.',
      },
    ],
  }
}

export function getAboutJsonLd(): JsonLdGraph {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        '@id': `${siteConfig.url}/about#page`,
        name: 'About Autoglow Detailing',
        url: `${siteConfig.url}/about`,
        about: {
          '@type': 'AutoRepair',
          '@id': `${siteConfig.url}/#business`,
          name: siteConfig.name,
        },
      },
    ],
  }
}

